import { useEffect, useRef, useState } from "react";
import { useTwoFactorRequestOTP } from "@/api/space/hooks/useSpace.2factor";
import { obterErroApi } from "@/utils/errors/apiError";
import { formatUiErrorMessage } from "@/utils/errors/uiErrorMessage";
import useAutenticacaoJwt from "../../../../../useAutenticacaoJwt";
import { mapearRespostaLoginParaUsuario } from "../../../../../utils/mapearUsuarioAutenticado";
import DialogoAutenticacaoDoisFatores from "../../../../AutenticacaoDoisFatores/DialogoAutenticacaoDoisFatores";
import type { DadosPendentesDoisFatores } from "../../loginGrupoFixo.types";

// --------------------------------- TYPES ---------------------------------
type DialogoOtpGrupoFixoProps = {
	readonly pendente: DadosPendentesDoisFatores;
	readonly aoFechar: () => void;
	readonly aoConcluir: () => void;
};

// ------------------------------- COMPONENT -------------------------------
export default function DialogoOtpGrupoFixo({ pendente, aoFechar, aoConcluir }: Readonly<DialogoOtpGrupoFixoProps>) {
	// -------------------------------- STATES --------------------------------
	const [erro, setErro] = useState<string | undefined>(undefined);
	const enviandoRef = useRef(false);
	const montadoRef = useRef(false);
	const { concluirEntrarDoisFatores } = useAutenticacaoJwt();

	// -------------------------------- QUERIES -------------------------------
	const mutation = useTwoFactorRequestOTP();

	// -------------------------------- EFFECTS -------------------------------
	useEffect(() => {
		montadoRef.current = true;
		return () => {
			montadoRef.current = false;
		};
	}, []);

	// -------------------------------- HANDLERS ------------------------------
	async function verificar(code: string, usernameDigitado?: string): Promise<void> {
		if (enviandoRef.current) return;
		setErro(undefined);
		const nome =
			pendente.showUsernameInput && usernameDigitado?.trim() ? usernameDigitado.trim() : pendente.username;
		if (!nome?.trim()) return setErro(formatUiErrorMessage("Usuário inválido para autenticação."));
		if (pendente.id_cad_pessoa_funcionario == null)
			return setErro(formatUiErrorMessage("id_cad_pessoa_funcionario não informado."));
		enviandoRef.current = true;
		try {
			const resposta = await mutation.mutateAsync({
				payload: {
					codigo_verificacao: code,
					id_cad_pessoa_funcionario: pendente.id_cad_pessoa_funcionario,
					nome_usuario: nome,
					groupId: pendente.groupId,
					usuario: pendente.eh_usuario_space ? pendente.username : nome,
				},
				isSpaceUser: pendente.eh_usuario_space,
			});

			if (!montadoRef.current) return;
			if (!resposta?.token) return setErro(formatUiErrorMessage("Falha ao autenticar: token não retornado."));

			const user = pendente.preAuthUser
				? { ...pendente.preAuthUser, eh_usuario_space: pendente.eh_usuario_space }
				: mapearRespostaLoginParaUsuario(resposta, {
						groupIdPadrao: pendente.groupId,
						ehUsuarioSpace: pendente.eh_usuario_space,
					});

			concluirEntrarDoisFatores?.({ user, access_token: resposta.token, groupId: pendente.groupId });
			aoConcluir();
		} catch (error: unknown) {
			if (montadoRef.current)
				setErro(formatUiErrorMessage(obterErroApi(error)?.message ?? "Código inválido. Tente novamente."));
		} finally {
			enviandoRef.current = false;
		}
	}

	// -------------------------------- RETURN --------------------------------
	return (
		<DialogoAutenticacaoDoisFatores
			aberto
			desabilitarFechamentoBackdrop
			carregando={mutation.isPending}
			mensagemErro={erro}
			placeholderNomeUsuario="Nome ou código do usuário SPACE"
			exibirCampoNomeUsuario={pendente.showUsernameInput}
			permitirBuscaUsuarioPorCodigo={pendente.showUsernameInput}
			groupId={pendente.groupId}
			aoFechar={aoFechar}
			aoVerificar={verificar}
		/>
	);
}
